import { decode as decodeBlurhash } from 'blurhash'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { Asset, AssetVariantTypeType } from '@/api/common-types'
import { clx } from '@/utils/clx'

// Render priority: prefer the smallest derivative that's good enough, then fall back
// up through bigger derivatives, then finally to the ORIGINAL.
const VARIANT_PRIORITY: AssetVariantTypeType[] = ['LARGE', 'MEDIUM', 'SMALL', 'THUMBNAIL', 'ORIGINAL']

const pickVariantUrl = (asset: Asset, preferred?: AssetVariantTypeType): string => {
	if (preferred) {
		const exact = asset.variants.find(v => v.type === preferred)
		if (exact) return exact.url
	}
	for (const type of VARIANT_PRIORITY) {
		const match = asset.variants.find(v => v.type === type)
		if (match) return match.url
	}
	return asset.variants[0]?.url ?? ''
}

type BlurhashCanvasProps = {
	hash: string
	width: number
	height: number
	className?: string
}

const BlurhashCanvas = ({ hash, width, height, className }: BlurhashCanvasProps) => {
	const canvasRef = useRef<HTMLCanvasElement>(null)
	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas) return
		try {
			const pixels = decodeBlurhash(hash, width, height)
			const ctx = canvas.getContext('2d')
			if (!ctx) return
			const imageData = ctx.createImageData(width, height)
			imageData.data.set(pixels)
			ctx.putImageData(imageData, 0, 0)
		} catch {
			// blurhash decode error is non-fatal — placeholder just stays empty.
		}
	}, [hash, width, height])
	return <canvas ref={canvasRef} width={width} height={height} className={className} />
}

export type AssetImageProps = {
	asset: Asset
	alt?: string
	preferredVariant?: AssetVariantTypeType
	className?: string
	imgClassName?: string
}

export const AssetImage = ({ asset, alt = '', preferredVariant, className, imgClassName }: AssetImageProps) => {
	const url = useMemo(() => pickVariantUrl(asset, preferredVariant), [asset, preferredVariant])
	const [loaded, setLoaded] = useState(false)
	const aspectStyle = useMemo<React.CSSProperties | undefined>(() => {
		if (asset.width && asset.height) {
			return { aspectRatio: `${asset.width} / ${asset.height}` }
		}
		return undefined
	}, [asset.width, asset.height])

	return (
		<div className={clx('relative overflow-hidden bg-ui-bg-subtle', className)} style={aspectStyle}>
			{asset.blurhash && !loaded && (
				<BlurhashCanvas
					hash={asset.blurhash}
					width={32}
					height={32}
					className='absolute inset-0 h-full w-full'
				/>
			)}
			<img
				src={url}
				alt={alt}
				onLoad={() => setLoaded(true)}
				className={clx(
					'relative h-full w-full object-cover transition-opacity duration-200',
					loaded ? 'opacity-100' : 'opacity-0',
					imgClassName
				)}
				loading='lazy'
				decoding='async'
			/>
		</div>
	)
}
